import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import useSearchResults from '../../../../hooks/SearchResults';
import AutoColumnsGrid from '../../../../new-components/display-system/AutoColumnsGrid';
import MapSearchResultsToCards from './MapSearchResultsToCards';
import NawyLoadingIndicator from '../../../../new-components/NawyLoadingIndicator';
import ErrorComponent from '../../../../new-components/ui/ErrorComponent';
import {
  handleChangePage,
  resetFilters
} from '../../../../helpers/search-filters';
import NoResultsIndicator from '../../../../new-components/NoResultsIndicator';
import {
  SearchContext,
  SearchContextInterface
} from '../../../search-new/context/SearchContext';

interface CardsContainerProps {
  loadedSearchResultsSSR: any;
  pageTitle: string;
  launches: any;
}

const CardsContainer = ({
  loadedSearchResultsSSR,
  pageTitle,
  launches
}: CardsContainerProps) => {
  const { t } = useTranslation(['search', 'common']);
  const router = useRouter();
  const { searchFiltersState, searchFiltersDispatch } = useContext(
    SearchContext
  ) as SearchContextInterface;
  const searchBodyResults = useSearchResults(
    searchFiltersState.searchBody,
    loadedSearchResultsSSR
  );

  const [allSearchResults, setAllSearchResults] = useState<any[]>(
    searchBodyResults.loadedSearchResults || []
  );

  const cardsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchBodyResults.loadedSearchResults) {
      if (
        searchFiltersState.page_number != 1 &&
        searchFiltersState.page_number != undefined
      ) {
        setAllSearchResults([
          ...allSearchResults,
          ...searchBodyResults.loadedSearchResults
        ]);
      } else {
        setAllSearchResults([...searchBodyResults.loadedSearchResults]);
      }
    }
  }, [searchBodyResults.loadedSearchResults]);

  const handleScroll = () => {
    if (!cardsContainerRef.current) return;
    const containerBottom =
      cardsContainerRef.current.getBoundingClientRect().bottom;
    if (containerBottom - 200 <= window.innerHeight) {
      handleChangePage(allSearchResults.length, searchFiltersDispatch);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const getPageTitle = () => {
    const header = t(searchBodyResults.ssrSearchType);

    if (!loadedSearchResultsSSR)
      return (
        <h1 className="headline-3 entity-title" data-cy="cards-title">
          {header}
        </h1>
      );
    else return <h2 className="headline-3 entity-title">{header}</h2>;
  };
  const getSearchResults = () => {
    if (searchBodyResults.isError) {
      return <ErrorComponent />;
    } else if (searchBodyResults.isFetching) {
      return <NawyLoadingIndicator />;
    } else if (searchBodyResults.loadedSearchResultsCount == 0) {
      return (
        <NoResultsIndicator
          onClick={() =>
            resetFilters(
              router,
              searchFiltersDispatch,
              loadedSearchResultsSSR ? false : true
            )
          }
          isResetButton={true}
        />
      );
    } else {
      return (
        <div ref={cardsContainerRef}>
          <AutoColumnsGrid
            minWidth={'390px'}
            rowsGap={'16px'}
            columnsGap={'16px'}
          >
            <MapSearchResultsToCards
              searchResults={allSearchResults}
              findType={
                searchBodyResults.ssrSearchType == 'properties'
                  ? 'property'
                  : 'compound'
              }
              pageNumber={searchFiltersState.page_number}
              searchLocation="Card"
              launches={launches}
            />
          </AutoColumnsGrid>
        </div>
      );
    }
  };
  const getPageHeader = () => {
    return (
      <h1 className="text-2">
        {pageTitle}{' '}
        <span>
          {searchBodyResults.loadedSearchResultsCount
            ? searchBodyResults.loadedSearchResultsCount + ' ' + t('results')
            : ''}
        </span>
      </h1>
    );
  };

  return (
    <Container>
      <div className="cards-container">
        {!searchBodyResults.isFetching && !searchBodyResults.isError && (
          <div className="title entity-title">
            {getPageTitle()}
            {getPageHeader()}
          </div>
        )}

        {getSearchResults()}
      </div>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  min-height: calc(100vh - 130px);
  position: relative;
  flex-direction: row;
  margin-inline-start: auto;
  width: 100%;
  transition: all 0.6s ease-out;

  .launches-banner-wrapper {
    padding: 32px 0;
    grid-column: 1/-1;
  }

  .cards-container {
    position: relative;
    flex: 1;
    padding-bottom: 16px;
    width: 100%;
    .title {
      display: flex;
      align-items: center;
      padding-top: 16px;

      h1 {
        margin-inline-start: 4px;
        margin-inline-end: 4px;
      }
    }
  }
  .pagination {
    padding: 40px 0px;
    position: absolute;
    bottom: -130px;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

export default CardsContainer;
